import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MessageCircle, MapPin, Clock, CheckCircle, Loader, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { SEOHead } from '@/components/SEOHead';
import { Reveal } from '@/components/Reveal';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const API = '/api';

const SERVICE_OPTIONS = [
  'Digital Solutions (Website, SEO, Marketing)',
  'Creative Solutions (Branding, Graphic Design, Content)',
  'Business Support (Admin, Back Office, Virtual Assistance)',
  'E-Commerce Support (Marketplace & Online Operations)',
  'Administrative Support',
  'Customer Support / CRM',
  'Finance & Accounting',
  'HR & Recruitment',
  'Healthcare Administration',
  'Business Research',
  'General Enquiry',
  'Other',
];

const EMPTY = {
  name: '', company: '', email: '', phone: '', country: '',
  service: '', message: '',
};

export default function Contact() {
  const s = useSiteSettings();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  /* ── Contact info — STRICTLY no hardcoded fallbacks ──────────── */
  const email = s.email || null;
  const phone = s.phone || null;
  const mobile = s.mobile || null;
  const whatsapp = s.social_whatsapp || null;
  const address = s.address || null;
  const businessHours = s.business_hours || null;
  const hasContact = email || phone || mobile || whatsapp || address;

  /* ── Form ─────────────────────────────────────────────────────── */
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Full name is required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid work email is required.';
    if (!form.message.trim()) e.message = 'Please tell us about your needs.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        company: form.company.trim() || undefined,
        country: form.country.trim() || undefined,
        service: form.service || undefined,
        message: form.message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 rounded-xl border text-[#1F2937] text-sm bg-white placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 transition-all duration-150 ${
      errors[field]
        ? 'border-red-400 focus:ring-red-200'
        : 'border-[#D1D5DB] focus:ring-[#0E5A57]/20 focus:border-[#0E5A57]'
    }`;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact NKP VENTURES — Let's Build Better Together"
        description="Contact NKP VENTURES to discuss digital and business solutions for India or reliable outsourcing support for your international operations."
        canonical="https://nkpventures.com/contact"
      />

      {/* Hero */}
      <section className="bg-[#0A1628] pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline-label text-[#D4AF37] mb-4">Let's Talk</p>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Let's Build Better Together.
            </h1>
            <p className="text-white/65 text-lg leading-relaxed max-w-2xl">
              Whether you need digital and business solutions in India or reliable outsourcing support for your international operations, we'd be happy to discuss how NKP VENTURES can help.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Form + Contact Info */}
      <section className="py-20 bg-[#F7FAFC]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* Form */}
            <Reveal className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-8 lg:p-10">
                {submitted ? (
                  <div className="flex flex-col items-center text-center py-8">
                    <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mb-5">
                      <CheckCircle className="w-7 h-7 text-[#0E5A57]" />
                    </div>
                    <h2 className="font-serif text-2xl font-bold text-[#0A1628] mb-3">
                      Thank you — we'll be in touch.
                    </h2>
                    <p className="text-[#6B7280] leading-relaxed max-w-sm">
                      We've received your message and will respond within one business day. We look forward to discussing how we can support your business.
                    </p>
                  </div>
                ) : (
                  <>
                    <h2 className="font-serif text-2xl font-bold text-[#0A1628] mb-2">Send us a message</h2>
                    <p className="text-[#6B7280] text-sm mb-8">Fields marked * are required.</p>

                    <form onSubmit={handleSubmit} noValidate className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            value={form.name}
                            onChange={e => set('name', e.target.value)}
                            placeholder="Your full name"
                            className={inputClass('name')}
                            autoComplete="name"
                          />
                          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">
                            Business / Company Name
                          </label>
                          <input
                            type="text"
                            value={form.company}
                            onChange={e => set('company', e.target.value)}
                            placeholder="Your company name"
                            className={inputClass('company')}
                            autoComplete="organization"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">
                            Work Email *
                          </label>
                          <input
                            type="email"
                            value={form.email}
                            onChange={e => set('email', e.target.value)}
                            placeholder="you@company.com"
                            className={inputClass('email')}
                            autoComplete="email"
                          />
                          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={e => set('phone', e.target.value)}
                            placeholder="+1 555 000 0000"
                            className={inputClass('phone')}
                            autoComplete="tel"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">
                            Country
                          </label>
                          <input
                            type="text"
                            value={form.country}
                            onChange={e => set('country', e.target.value)}
                            placeholder="United Kingdom"
                            className={inputClass('country')}
                            autoComplete="country-name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">
                            Service / Area of Interest
                          </label>
                          <select
                            value={form.service}
                            onChange={e => set('service', e.target.value)}
                            className={`${inputClass('service')} cursor-pointer`}
                          >
                            <option value="">Select an area...</option>
                            {SERVICE_OPTIONS.map(o => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">
                          Message *
                        </label>
                        <textarea
                          value={form.message}
                          onChange={e => set('message', e.target.value)}
                          rows={5}
                          placeholder="Tell us about your business and what you're looking for..."
                          className={`${inputClass('message')} resize-none`}
                        />
                        {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full flex items-center justify-center gap-2 bg-[#0A1628] hover:bg-[#0F2847] disabled:opacity-60 text-white font-semibold py-4 rounded-full transition-colors duration-200"
                      >
                        {submitting ? (
                          <><Loader className="w-4 h-4 animate-spin" /> Sending...</>
                        ) : (
                          <>Send Message <ArrowRight className="w-4 h-4" /></>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </Reveal>

            {/* Sidebar — only shows configured data */}
            <Reveal delay={0.1} className="lg:col-span-2 space-y-5">
              {/* Contact details — only rendered if at least one field exists */}
              {hasContact && (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-7">
                  <h3 className="font-serif text-lg font-bold text-[#0A1628] mb-6">Contact Details</h3>
                  <div className="space-y-5">
                    {email && (
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-[#0E5A57]" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-0.5">Email</p>
                          <a href={`mailto:${email}`} className="text-sm text-[#374151] hover:text-[#0E5A57] transition-colors break-all">
                            {email}
                          </a>
                        </div>
                      </div>
                    )}
                    {phone && (
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <Phone className="w-4 h-4 text-[#0E5A57]" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-0.5">Phone</p>
                          <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-sm text-[#374151] hover:text-[#0E5A57] transition-colors">
                            {phone}
                          </a>
                        </div>
                      </div>
                    )}
                    {mobile && mobile !== phone && (
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <Phone className="w-4 h-4 text-[#0E5A57]" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-0.5">Mobile</p>
                          <a href={`tel:${mobile.replace(/\s/g, '')}`} className="text-sm text-[#374151] hover:text-[#0E5A57] transition-colors">
                            {mobile}
                          </a>
                        </div>
                      </div>
                    )}
                    {whatsapp && (
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="w-4 h-4 text-[#0E5A57]" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-0.5">WhatsApp</p>
                          <a href={whatsapp} target="_blank" rel="noopener noreferrer" className="text-sm text-[#374151] hover:text-[#0E5A57] transition-colors">
                            Message us on WhatsApp
                          </a>
                        </div>
                      </div>
                    )}
                    {address && (
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-[#0E5A57]" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-0.5">Address</p>
                          <p className="text-sm text-[#374151] leading-relaxed">{address}</p>
                        </div>
                      </div>
                    )}
                    {businessHours && (
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <Clock className="w-4 h-4 text-[#0E5A57]" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-0.5">Business Hours</p>
                          <p className="text-sm text-[#374151] leading-relaxed">{businessHours}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* CTAs — only if data configured */}
                  {(email || phone || whatsapp) && (
                    <div className="mt-6 pt-6 border-t border-[#F3F4F6] flex flex-wrap gap-2">
                      {email && (
                        <a
                          href={`mailto:${email}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E5A57] bg-teal-50 hover:bg-teal-100 px-3 py-2 rounded-full transition-colors duration-150"
                        >
                          <Mail className="w-3.5 h-3.5" /> Email Us
                        </a>
                      )}
                      {phone && (
                        <a
                          href={`tel:${phone.replace(/\s/g, '')}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E5A57] bg-teal-50 hover:bg-teal-100 px-3 py-2 rounded-full transition-colors duration-150"
                        >
                          <Phone className="w-3.5 h-3.5" /> Call Us
                        </a>
                      )}
                      {whatsapp && (
                        <a
                          href={whatsapp}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E5A57] bg-teal-50 hover:bg-teal-100 px-3 py-2 rounded-full transition-colors duration-150"
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* What Happens Next */}
              <div className="bg-[#0A1628] rounded-2xl p-7">
                <h3 className="font-serif text-base font-bold text-white mb-5">What happens next?</h3>
                <div className="space-y-4">
                  {[
                    { n: '01', title: 'We review your message', desc: 'We read every enquiry carefully and assess how we can best help.' },
                    { n: '02', title: 'We respond within 1 business day', desc: 'You\'ll receive a considered response — not an automated acknowledgement.' },
                    { n: '03', title: 'We schedule a call', desc: 'If appropriate, we\'ll arrange a short discovery call to understand your needs.' },
                    { n: '04', title: 'We propose a solution', desc: 'We\'ll outline exactly how NKP VENTURES can support your business.' },
                  ].map((step) => (
                    <div key={step.n} className="flex gap-4">
                      <span className="font-mono text-xs font-bold text-[#D4AF37] mt-0.5 w-6 flex-shrink-0">{step.n}</span>
                      <div>
                        <p className="text-white text-sm font-semibold mb-0.5">{step.title}</p>
                        <p className="text-white/50 text-xs leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
